'use client'

import type { DetailedProposal } from '@moment/horizon-data-access/proposal'

import { useCallback, useState } from 'react'

import { useDeleteProposalOrderMutation } from '@/legacy-api/proposals-slice'
import { extractOrderDetails } from '@/utils/proposals'

interface TradeToRemove {
  id: string
  name: string
  percentage: string
  accountName: string
}

interface UseCancelProposalOrderProps {
  proposal: DetailedProposal | undefined
  onOrderRemoved?: () => void
}

interface UseCancelProposalOrderReturn {
  handleRemoveOrder: (orderId: string) => Promise<void>
  handleRemoveClick: (orderId: string) => void
  isRemoveDialogOpen: boolean
  setIsRemoveDialogOpen: (isOpen: boolean) => void
  selectedTradeToRemove: TradeToRemove | null
}

export function useCancelProposalOrder({
  proposal,
  onOrderRemoved,
}: UseCancelProposalOrderProps): UseCancelProposalOrderReturn {
  const [isRemoveDialogOpen, setIsRemoveDialogOpen] = useState(false)
  const [selectedTradeToRemove, setSelectedTradeToRemove] =
    useState<TradeToRemove | null>(null)
  const [deleteProposalOrder] = useDeleteProposalOrderMutation()

  const handleRemoveOrder = useCallback(
    async (orderId: string) => {
      if (!proposal) return

      try {
        await deleteProposalOrder({
          proposal_id: proposal.proposal_id,
          proposal_order_id: orderId,
        }).unwrap()

        onOrderRemoved?.()
        setIsRemoveDialogOpen(false)
        setSelectedTradeToRemove(null)
      } catch (error) {
        // biome-ignore lint/complexity/noUselessCatch: Bulk suppression as part of biome migration
        throw error
      }
    },
    [deleteProposalOrder, proposal, onOrderRemoved],
  )

  const handleRemoveClick = useCallback(
    (orderId: string) => {
      if (!proposal) return

      const orderDetails = extractOrderDetails(proposal, orderId)
      if (!orderDetails) return

      setSelectedTradeToRemove({
        id: orderId,
        name: orderDetails.description ?? '',
        percentage: orderDetails.ytw ? `${orderDetails.ytw}` : '',
        accountName: proposal.name ?? '',
      })
      setIsRemoveDialogOpen(true)
    },
    [proposal],
  )

  return {
    handleRemoveOrder,
    handleRemoveClick,
    isRemoveDialogOpen,
    setIsRemoveDialogOpen,
    selectedTradeToRemove,
  }
}
